import { create } from 'zustand'
import type { MemberDto, SetupRequest } from '@familynest/shared'
import { api } from '../lib/api'

/**
 * Globaler Auth-Zustand. `init()` prüft zuerst die Erst-Einrichtung, dann die
 * bestehende Session (/me). Rolle steuert (zusätzlich zur serverseitigen RBAC)
 * die UI-Sichtbarkeit.
 */
type AuthState = {
  member: MemberDto | null
  status: 'loading' | 'setup' | 'anon' | 'authed'
  error: string | null
  init: () => Promise<void>
  setup: (payload: SetupRequest) => Promise<void>
  login: (name: string, secret: string) => Promise<void>
  logout: () => Promise<void>
}

export const useAuth = create<AuthState>((set) => ({
  member: null,
  status: 'loading',
  error: null,

  async init() {
    try {
      if (await api.setupStatus()) {
        set({ member: null, status: 'setup' })
        return
      }
      const member = await api.me()
      set(member ? { member, status: 'authed', error: null } : { member: null, status: 'anon' })
    } catch {
      set({ member: null, status: 'anon' })
    }
  },

  async setup(payload) {
    set({ error: null })
    try {
      const member = await api.setup(payload)
      set({ member, status: 'authed', error: null })
    } catch {
      set({ error: 'Einrichtung fehlgeschlagen. Bitte Eingaben prüfen.' })
    }
  },

  async login(name, secret) {
    set({ error: null })
    try {
      const member = await api.login(name, secret)
      set({ member, status: 'authed', error: null })
    } catch {
      set({ error: 'Name oder Passwort/PIN falsch.' })
    }
  },

  async logout() {
    await api.logout()
    set({ member: null, status: 'anon' })
  },
}))

export const isAdult = (m: MemberDto | null): boolean => m?.role === 'adult'
export const isGuest = (m: MemberDto | null): boolean => m?.role === 'guest'
